import React, {ReactElement, ReactNode} from 'react';
import {Link} from 'gatsby';

import {validatePhoenixURL, useAllPagesData} from '../../utils/routes/phoenixPaths';
import {useLocale} from '../../RootProvider/LocaleContext';
import getHrefLanguageFromLocale from '../../localeUtils/getHrefLanguageFromLocale';

interface Props {
  children: ReactNode;
  href: string;
  onClick?: (ev: React.MouseEvent<HTMLAnchorElement>) => void;
  className?: string;
  activeClassName?: string;
  testId?: string;
  ariaLabel?: string;
  blank?: boolean;
  title?: string;
}

// To be used only when we are unsure of the link being rendered (CMS content)
// always favour using <Link> directly
function DynamicLink({
  children,
  href,
  onClick,
  className,
  activeClassName,
  ariaLabel,
  testId,
  blank,
  title,
}: Props): ReactElement {
  const pagesData = useAllPagesData();
  const internalURLPattern = /^(https?:)?(\/\/)?([\w-]*\.)*fundingoptions\.com(\/)?/i;
  const locale = useLocale();

  if (!href) {
    // this only happens on build occasionally for contentful previews
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  const isInternalLink = href.startsWith('/') || href.match(internalURLPattern);
  const isPhoenixURL = isInternalLink && validatePhoenixURL(href, pagesData);
  const url = isPhoenixURL ? href.replace(internalURLPattern, '/') : href;

  const language = getHrefLanguageFromLocale(locale);

  // handle fundingoptions urls
  if (isPhoenixURL) {
    return (
      <Link
        activeClassName={activeClassName}
        aria-label={ariaLabel}
        className={className}
        data-testid={testId}
        hrefLang={language}
        onClick={onClick}
        title={title}
        to={url}
      >
        {children}
      </Link>
    );
  }

  if (isInternalLink) {
    return (
      <a
        aria-label={ariaLabel}
        className={className}
        data-testid={testId}
        href={url}
        hrefLang={language}
        onClick={onClick}
        title={title}
      >
        {children}
      </a>
    );
  }

  // external link
  return (
    <a
      aria-label={ariaLabel}
      className={className}
      data-testid={testId}
      href={url}
      hrefLang={language}
      onClick={onClick}
      rel="nofollow noopener noreferrer"
      target={blank ? '_blank' : '_self'}
      title={title}
    >
      {children}
    </a>
  );
}

export default DynamicLink;
